import { useState } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { PinOff, Trash2 } from 'lucide-react';
import type { Note } from '@bits2schema/shared';
import { useT } from '../../i18n';
import { visibleColumns } from '../geometry';
import { COLOR_TOKENS, colorTokenBackgroundVar, colorTokenBorderVar, colorTokenLabelKey } from '../palette';
import { ContextMenu } from '../ContextMenu';
import type { TableFlowNode } from '../types';

/** E6-US1/US2/US3/US6/US7 — vykreslení tabulky, řádků sloupců a jejich značek poznámek. */
export function TableNode({ data }: NodeProps<TableFlowNode>) {
  const t = useT();
  const {
    table,
    color,
    viewSettings,
    columnNotes,
    tableNoteCount,
    isSelected,
    highlightedColumnId,
    dragOriginColumn,
    onRecolor,
    onOpenMenu,
    onChangeNoteText,
    onDeleteNote,
    onUnpinNote,
  } = data;
  const [paletteAnchor, setPaletteAnchor] = useState<{ x: number; y: number } | null>(null);
  const [openColumnNotes, setOpenColumnNotes] = useState<{ columnId: string; position: { x: number; y: number } } | null>(
    null,
  );
  const rows = visibleColumns(table, viewSettings);

  const style = color
    ? { background: colorTokenBackgroundVar(color), borderColor: colorTokenBorderVar(color) }
    : undefined;

  return (
    <div className={isSelected ? 'table-node table-node--selected' : 'table-node'} style={style}>
      {/*
        Záložní handle pro FK hrany kotvící na sloupec, který je zrovna
        skrytý přepínačem `showNonKeyColumns` (Epic 8) — bez něj by React
        Flow na chybějící handle vyhodilo "Couldn't create edge" (error
        #008). Neinteraktivní, používá se jen jako `sourceHandle`/
        `targetHandle` fallback (`CanvasView.tsx`).
      */}
      <Handle type="source" position={Position.Right} id="__table__" className="table-node__handle" isConnectable={false} />
      <div
        className="table-node__header"
        onContextMenu={(event) => {
          event.preventDefault();
          onOpenMenu({ kind: 'table', tableId: table.id, position: { x: event.clientX, y: event.clientY } });
        }}
      >
        <button
          type="button"
          className="table-node__color-swatch"
          style={style}
          title={t('canvas.table.colorButton')}
          onClick={(event) => {
            event.stopPropagation();
            const rect = event.currentTarget.getBoundingClientRect();
            setPaletteAnchor({ x: rect.left, y: rect.bottom + 4 });
          }}
        />
        <span className="table-node__title">{table.name}</span>
        {tableNoteCount > 0 && (
          <span className="note-marker" title={t('canvas.note.countTooltip', { count: tableNoteCount })}>
            {tableNoteCount}
          </span>
        )}
      </div>

      {paletteAnchor && (
        <ContextMenu position={paletteAnchor} onClose={() => setPaletteAnchor(null)}>
          <div className="color-palette">
            <button
              type="button"
              className="color-palette__swatch color-palette__swatch--none"
              title={t('canvas.color.none')}
              onClick={() => {
                onRecolor(table.id, null);
                setPaletteAnchor(null);
              }}
            />
            {COLOR_TOKENS.map((token) => (
              <button
                key={token}
                type="button"
                className="color-palette__swatch"
                style={{ background: colorTokenBackgroundVar(token), borderColor: colorTokenBorderVar(token) }}
                title={t(colorTokenLabelKey(token))}
                onClick={() => {
                  onRecolor(table.id, token);
                  setPaletteAnchor(null);
                }}
              />
            ))}
          </div>
        </ContextMenu>
      )}

      <div className="table-node__columns">
        {rows.map((column) => {
          const isPk = table.primaryKey?.columns.includes(column.name) ?? false;
          const notes = columnNotes[column.id] ?? [];
          const isHighlighted = column.id === highlightedColumnId;
          const isDragOrigin = dragOriginColumn?.tableId === table.id && dragOriginColumn.columnId === column.id;
          const compatibility =
            dragOriginColumn && !isDragOrigin
              ? (column.canonicalType ?? column.rawType) === dragOriginColumn.type
                ? 'compatible'
                : 'incompatible'
              : null;
          const classNames = ['table-node__column'];
          if (isHighlighted) classNames.push('table-node__column--highlighted');
          if (isDragOrigin) classNames.push('table-node__column--drag-origin');
          if (compatibility) classNames.push(`table-node__column--${compatibility}`);
          return (
            <div
              key={column.id}
              className={classNames.join(' ')}
              onContextMenu={(event) => {
                event.preventDefault();
                onOpenMenu({
                  kind: 'column',
                  tableId: table.id,
                  columnId: column.id,
                  position: { x: event.clientX, y: event.clientY },
                });
              }}
            >
              <Handle
                type="source"
                position={Position.Right}
                id={column.id}
                className="table-node__column-handle"
              />
              <span
                className={
                  isPk ? 'table-node__column-name table-node__column-name--pk' : 'table-node__column-name'
                }
              >
                {column.name}
              </span>
              {viewSettings.showColumnType && (
                <span className="table-node__column-type">{column.canonicalType ?? column.rawType}</span>
              )}
              {viewSettings.showNullable && (
                <span className="table-node__column-nullable">
                  {column.nullable ? t('canvas.column.nullable') : t('canvas.column.notNullable')}
                </span>
              )}
              {viewSettings.showDefault && column.defaultValue !== null && (
                <span className="table-node__column-default" title={column.defaultValue}>
                  = {column.defaultValue}
                </span>
              )}
              {viewSettings.showComment && column.comment !== null && (
                <span className="table-node__column-comment" title={column.comment}>
                  i
                </span>
              )}
              {notes.length > 0 && (
                <button
                  type="button"
                  className="note-marker note-marker--column"
                  title={t('canvas.note.countTooltip', { count: notes.length })}
                  onClick={(event) => {
                    event.stopPropagation();
                    setOpenColumnNotes({ columnId: column.id, position: { x: event.clientX, y: event.clientY } });
                  }}
                >
                  {notes.length}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {openColumnNotes && (
        <ColumnNotesPopover
          position={openColumnNotes.position}
          notes={columnNotes[openColumnNotes.columnId] ?? []}
          onClose={() => setOpenColumnNotes(null)}
          onChangeText={onChangeNoteText}
          onDelete={onDeleteNote}
          onUnpin={onUnpinNote}
        />
      )}
    </div>
  );
}

interface ColumnNotesPopoverProps {
  position: { x: number; y: number };
  notes: Note[];
  onClose: () => void;
  onChangeText: (noteId: string, text: string) => void;
  onDelete: (noteId: string) => void;
  onUnpin: (noteId: string) => void;
}

/** Sloupcová poznámka nemá vlastní RF node (epic-6 kap. 8) — správa jde přes tento popover. */
function ColumnNotesPopover({ position, notes, onClose, onChangeText, onDelete, onUnpin }: ColumnNotesPopoverProps) {
  const t = useT();
  return (
    <ContextMenu position={position} onClose={onClose}>
      <div className="column-notes-popover">
        {notes.map((note) => (
          <div key={note.id} className="column-notes-popover__item">
            <textarea
              className="note-bubble__text"
              defaultValue={note.text}
              placeholder={t('canvas.note.placeholder')}
              onBlur={(event) => {
                if (event.target.value !== note.text) onChangeText(note.id, event.target.value);
              }}
            />
            <div className="column-notes-popover__actions">
              <button type="button" onClick={() => onUnpin(note.id)}>
                <PinOff size={14} />
                {t('canvas.note.unpin')}
              </button>
              <button type="button" className="danger" onClick={() => onDelete(note.id)}>
                <Trash2 size={14} />
                {t('common.delete')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </ContextMenu>
  );
}
